/**
 * Utility functions for image cropping and user photo uploads
 * (Cloudinary by default; private S3 staging when allowlisted).
 */

import {
  buildCropUploadRef,
  fetchStagingUploadStatus,
  isStagingObjectKey,
  isStagingUploadEnabled,
  uploadToStaging,
} from '../services/stagingUploadService';
import { isMongoMediaId } from './placeOrderImageUtils';
import { detectFaces } from './faceDetector';
import {
  PAGE_ASPECT_RATIO,
  centerCropRect,
  cropDataForImage,
  cropReviewFlags,
  cropReviewMetrics,
  largestCropSize,
  skinSplit,
  subjectBoxFromFaces,
  toCropperData,
  zoomedCropRect,
} from './autoCropRect';

/** A crop screen couldn't save a crop (decided 2026-11-02: plain words; the reason goes to the console). */
export const CROP_SAVE_FAILED_MESSAGE = "We couldn't save your crop. Check your connection and try again.";

const CLOUD_NAME = process.env.REACT_APP_CLOUD_NAME;
const UPLOAD_PRESET = 'unsigned_uploads';

/**
 * Switch for the face-centred auto-crop, in order of precedence:
 *   1. `localStorage.md_smartCrop` "0"/"1": per-browser override for testing;
 *   2. the API's kill switch (`smartCrop` on /media/staging-upload-status,
 *      from SMART_CROP_ENABLED on the server): off for everyone in one
 *      restart, no rebuild;
 *   3. this constant, when the API does not say (older API, request failed).
 */
const SMART_CROP_ENABLED = true;

/**
 * Key for the crop screens' cache of photo blob URLs: the record id plus the
 * photo in it. The S3 upload keeps one cover record per book and puts a
 * replacement photo in it, so an id alone handed the crop screen the photo
 * that was replaced.
 */
export const cropCacheKey = (image) =>
  `${image?._id || image?.id || ''}|${image?.stagingKey || image?.sourceStagingKey || image?.publicId || ''}`;

export const isSmartCropEnabled = async () => {
  try {
    const override = window.localStorage.getItem('md_smartCrop');
    if (override === '0') return false;
    if (override === '1') return true;
  } catch (err) {
    // Private mode or storage blocked: use the server/default.
  }
  try {
    const status = await fetchStagingUploadStatus();
    if (typeof status?.smartCrop === 'boolean') return status.smartCrop;
  } catch (err) {
    // Status unavailable: fall through to the default.
  }
  return SMART_CROP_ENABLED;
};

/** Width of the JPEG the grid and the line-art run receive. */
const CROP_OUTPUT_WIDTH = 800;
const CROP_OUTPUT_QUALITY = 0.9;

const loadImage = (imageUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${imageUrl}`));
    img.src = imageUrl;
  });

/** How long a batch waits for the face detector to finish loading. */
const FACE_DETECTOR_WAIT_MS = 10000;
/** Faces below this confidence do not move the crop; the review rules still see them. */
const CROP_FACE_MIN_SCORE = 0.5;

/** Longest edge of the copy the face detector analyses (its input is 192px). */
const ANALYSIS_MAX_EDGE = 512;

const downscaleForAnalysis = (img) => {
  const scale = Math.min(1, ANALYSIS_MAX_EDGE / Math.max(img.width, img.height));
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(img, 0, 0, width, height);
  return { canvas, width, height, scale: width / img.width };
};

/**
 * Draw `rect` (source pixels) of `img` onto a canvas of exactly the target
 * ratio and encode it as JPEG. The canvas size fixes the output ratio
 * whatever the rectangle's rounding.
 */
const renderCropToBlob = (img, rect, targetAspectRatio) =>
  new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = CROP_OUTPUT_WIDTH;
      canvas.height = CROP_OUTPUT_WIDTH / targetAspectRatio;
      ctx.drawImage(
        img,
        rect.x,
        rect.y,
        rect.width,
        rect.height,
        0,
        0,
        canvas.width,
        canvas.height
      );
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        },
        'image/jpeg',
        CROP_OUTPUT_QUALITY
      );
    } catch (error) {
      reject(error);
    }
  });

/**
 * Auto-crop image to the page ratio (center crop)
 * @param {string} imageUrl - Source image URL
 * @param {number} targetAspectRatio - Target aspect ratio (default: page)
 * @returns {Promise<Blob>} - Cropped image blob
 */
export const autoCropImage = async (imageUrl, targetAspectRatio = PAGE_ASPECT_RATIO) => {
  const img = await loadImage(imageUrl);
  const rect = centerCropRect(img.width, img.height, targetAspectRatio);
  if (!rect) throw new Error(`Image has no usable size: ${img.width}x${img.height}`);
  return renderCropToBlob(img, rect, targetAspectRatio);
};

/**
 * Cut a page crop again from the full photo at a saved crop box (`cropData`,
 * as the auto crop or the crop tool recorded it). For a crop that was cut in
 * the browser but never reached the server, once the cut copy is gone (the
 * page was reloaded). With no usable box it centres, like autoCropImage.
 */
export const recutCropFromData = async (imageUrl, cropData, targetAspectRatio = PAGE_ASPECT_RATIO) => {
  const img = await loadImage(imageUrl);
  const data = cropDataForImage(cropData, img.naturalWidth || img.width, img.naturalHeight || img.height);
  const rect =
    data && data.width > 0 && data.height > 0
      ? { x: data.x, y: data.y, width: data.width, height: data.height }
      : centerCropRect(img.width, img.height, targetAspectRatio);
  if (!rect) throw new Error(`Image has no usable size: ${img.width}x${img.height}`);
  return renderCropToBlob(img, rect, targetAspectRatio);
};

/**
 * Auto-crop with the window centred on the faces MediaPipe finds when the
 * smart crop is on, else in the centre. Any detector problem falls back to
 * the centre crop, so this never fails where autoCropImage would not.
 *
 * @returns {Promise<{blob: Blob, cropData: object, method: string, faceCount: number, review: object|null}>}
 *   `cropData` is in source pixels in the shape cropperjs `getData()` uses,
 *   so the cropper can open pre-positioned on it. `review` says whether the
 *   crop is worth a look ({flagged, reasons, metrics}).
 */
export const autoCropImageWithRect = async (imageUrl, targetAspectRatio = PAGE_ASPECT_RATIO) => {
  const img = await loadImage(imageUrl);
  let rect = null;
  let method = 'center';

  let faceCount = 0;
  // What the review rules look at; filled in along the way.
  let fullFaces = [];
  let possibleFaces = [];
  let subject = null;
  let small = null;
  let detectorAvailable = true;
  const smartCropOn = await isSmartCropEnabled();
  if (smartCropOn) {
    try {
      // Detect on a small copy: a 5MP photo (×20 in a batch) is the slow part.
      small = downscaleForAnalysis(img);
      // Faces anchor the window: a body box is estimated from each face, the
      // window is centred on the box around all of them, and when that subject
      // is small in the frame the window zooms in (within a print-safe limit).
      // Edge/colour saliency was tried and drifted off people on real photos,
      // so with no face found (or no detector yet) we centre.
      const detected = await detectFaces(small.canvas, { waitMs: FACE_DETECTOR_WAIT_MS });
      if (detected) {
        const k = 1 / small.scale;
        const toFull = (f) => ({ x: f.x * k, y: f.y * k, width: f.width * k, height: f.height * k, score: f.score });
        fullFaces = detected.filter((f) => (f.score ?? 1) >= CROP_FACE_MIN_SCORE).map(toFull);
        possibleFaces = detected.filter((f) => (f.score ?? 1) < CROP_FACE_MIN_SCORE).map(toFull);
      }
      if (fullFaces.length) {
        faceCount = fullFaces.length;
        subject = subjectBoxFromFaces(fullFaces, img.width, img.height);
        if (subject) rect = zoomedCropRect(subject, img.width, img.height, targetAspectRatio);
        if (rect) method = 'faces';
      } else if (detected === null) {
        detectorAvailable = false;
        method = 'center-no-detector';
      }
    } catch (err) {
      console.warn('Smart crop failed; using center crop:', err);
    }
  }

  if (!rect) {
    rect = centerCropRect(img.width, img.height, targetAspectRatio);
    if (!rect) throw new Error(`Image has no usable size: ${img.width}x${img.height}`);
  }

  // "Worth a look?" Measured on the same small copy; never throws.
  let review = null;
  if (smartCropOn) {
    try {
      let skin = null;
      if (small) {
        const ctx = small.canvas.getContext('2d');
        const { data } = ctx.getImageData(0, 0, small.width, small.height);
        const s = small.scale;
        skin = skinSplit(data, small.width, small.height, {
          x: rect.x * s, y: rect.y * s, width: rect.width * s, height: rect.height * s,
        });
      }
      const full = largestCropSize(img.width, img.height, targetAspectRatio);
      const metrics = cropReviewMetrics({
        faces: fullFaces, possibleFaces, subject, rect, imageWidth: img.width, imageHeight: img.height, skin,
        zoom: full ? full.width / rect.width : 1,
      });
      review = { ...cropReviewFlags(metrics, { detectorAvailable }), metrics };
    } catch (err) {
      console.warn('Crop review failed:', err);
    }
  }

  const blob = await renderCropToBlob(img, rect, targetAspectRatio);
  return {
    blob,
    cropData: toCropperData(rect, { width: img.width, height: img.height }),
    method,
    faceCount,
    review,
  };
};

/**
 * Check if image dimensions match 8:11 aspect ratio (with tolerance)
 * @param {string} imageUrl - Image URL to check
 * @param {number} targetAspectRatio - Target aspect ratio (default: 8/11)
 * @param {number} tolerance - Tolerance for aspect ratio match (default: 0.01)
 * @returns {Promise<boolean>} - True if image matches aspect ratio
 */
export const isCorrectAspectRatio = async (imageUrl, targetAspectRatio = 8.75 / 11.5, tolerance = 0.01) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const imageAspectRatio = img.width / img.height;
      const difference = Math.abs(imageAspectRatio - targetAspectRatio);
      const matches = difference <= tolerance;
      
      console.log(`Image dimensions: ${img.width}x${img.height}, ratio: ${imageAspectRatio.toFixed(4)}, target: ${targetAspectRatio.toFixed(4)}, matches: ${matches}`);
      
      resolve(matches);
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image for ratio check: ${imageUrl}`));
    };

    img.src = imageUrl;
  });
};

/**
 * Upload a user photo to S3 staging (allowlisted) or Cloudinary (everyone else).
 */
export const uploadUserImage = async (
  fileOrBlob,
  filename = 'image.jpg',
  { bookId, variant = 'original', isCover = false, mediaId = null, cropData = null, cropReview = null } = {},
) => {
  const useStaging = bookId && (await isStagingUploadEnabled());
  if (useStaging) {
    return uploadToStaging(fileOrBlob, bookId, variant, filename, {
      isCover,
      mediaId,
      cropData,
      cropReview,
    });
  }
  return uploadToCloudinary(fileOrBlob, filename);
};

export const uploadToCloudinary = async (fileOrBlob, filename = 'image.jpg') => {
  try {
    const formData = new FormData();
    
    // Convert blob to file if needed
    const file = fileOrBlob instanceof File 
      ? fileOrBlob 
      : new File([fileOrBlob], filename, { type: 'image/jpeg' });

    formData.append('file', file);
    formData.append('upload_preset', UPLOAD_PRESET);

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      url: data.secure_url,
      publicId: data.public_id
    };
  } catch (error) {
    console.error('Cloudinary upload error:', error);
    throw error;
  }
};

function resolveMongoMediaId(image) {
  if (!image) return null;
  if (image._id && isMongoMediaId(String(image._id))) return String(image._id);
  if (image.id && isMongoMediaId(String(image.id))) return String(image.id);
  return null;
}

function buildStagingRunpodItem({ image, url, stagingKey, mongoMediaId, originalMediaId }) {
  if (mongoMediaId && isMongoMediaId(mongoMediaId)) {
    return {
      mediaId: mongoMediaId,
      url,
      oldPublicId: image.sourceStagingKey || image.stagingKey || originalMediaId || stagingKey,
    };
  }
  return { mediaId: stagingKey, url, oldPublicId: originalMediaId };
}

/**
 * Process image for /run endpoint
 * - If image has blob URL or is manually cropped: upload blob to Cloudinary
 * - If image is not 8:11 ratio: auto-crop and upload
 * - If image is already 8:11 ratio: use as-is
 * 
 * @param {Object} image - Image object from coverImages array
 * @param {Object} [options] - { isCover: boolean }
 * @returns {Promise<{mediaId: string, url: string}>} - Object with mediaId and Cloudinary URL ready for /run endpoint
 */
export const processImageForRun = async (image, bookId, { isCover = false } = {}) => {
  const mongoMediaId = resolveMongoMediaId(image);
  const originalMediaId =
    mongoMediaId ||
    image.stagingKey ||
    image.publicId ||
    image.public_id ||
    (image.AIinfo?.public_id) ||
    image._id ||
    image.id;

  const uploadOptions = {
    bookId,
    variant: 'cropped',
    isCover,
    mediaId: isCover ? null : mongoMediaId,
  };

  try {
    // Scenario 0: Cropped preview already uploaded (S3 staging or Cloudinary)
    if (image.croppedCloudinaryUrl || image.croppedStagingKey) {
      const cropRef =
        typeof image.croppedCloudinaryUrl === 'object'
          ? image.croppedCloudinaryUrl
          : image.croppedCloudinaryUrl
            ? {
                url: image.croppedCloudinaryUrl,
                publicId: image.croppedStagingKey || originalMediaId,
                stagingKey: image.croppedStagingKey || null,
              }
            : null;
      const stagingKey =
        image.croppedStagingKey ||
        cropRef?.stagingKey ||
        cropRef?.publicId;
      // Never fall back to image.url — grid thumbnails often share the wrong preview.
      const url = cropRef?.url;

      if (stagingKey && url && isStagingObjectKey(stagingKey)) {
        console.log(' Using pre-uploaded cropped staging key:', stagingKey);
        return buildStagingRunpodItem({
          image,
          url,
          stagingKey,
          mongoMediaId,
          originalMediaId,
        });
      }

      if (url && cropRef?.publicId) {
        console.log(' Using pre-uploaded cropped Cloudinary URL:', url);
        return { mediaId: cropRef.publicId, url, oldPublicId: originalMediaId };
      }
    }

    // Check if image has blob URL (manually cropped)
    const isBlobUrl = image.url?.startsWith('blob:');
    const isCropped = image.isCropped === true;

    console.log('Processing image:', {
      filename: image.filename,
      isBlobUrl,
      isCropped,
      url: image.url
    });

    // Scenario 1: Manually cropped (has blob URL or isCropped flag)
    if (isBlobUrl || (isCropped && image.file)) {
      console.log(' Uploading manually cropped image...');
      
      let fileToUpload = image.file;
      
      if (!fileToUpload && isBlobUrl) {
        const response = await fetch(image.url);
        const blob = await response.blob();
        fileToUpload = new File([blob], image.filename || 'cropped-image.jpg', {
          type: 'image/jpeg'
        });
      }

      const uploadResult = await uploadUserImage(
        fileToUpload,
        image.filename || image.originalFilename || 'cropped-image.jpg',
        uploadOptions,
      );
      const cropRef = buildCropUploadRef(uploadResult) || uploadResult;
      const uploadedKey = cropRef.stagingKey || cropRef.publicId;

      if (isStagingObjectKey(uploadedKey) && mongoMediaId) {
        return buildStagingRunpodItem({
          image,
          url: cropRef.url,
          stagingKey: uploadedKey,
          mongoMediaId,
          originalMediaId,
        });
      }

      console.log(' Uploaded manually cropped image:', cropRef.url, 'publicId:', cropRef.publicId);
      return { mediaId: cropRef.publicId, url: cropRef.url, oldPublicId: originalMediaId };
    }

    // Scenario 2: Check if existing image needs cropping
    const sourceUrl = image.originalUrl || image.url;
    
    console.log(' Checking aspect ratio for:', sourceUrl);
    
    const isCorrectRatio = await isCorrectAspectRatio(sourceUrl);
    
    if (isCorrectRatio) {
      console.log(' Image already has 8:11 ratio, using as-is:', sourceUrl);
      const runStagingKey =
        image.croppedStagingKey ||
        image.stagingKey ||
        image.publicId ||
        image.public_id ||
        null;

      if (isStagingObjectKey(runStagingKey) && mongoMediaId) {
        return buildStagingRunpodItem({
          image,
          url: sourceUrl,
          stagingKey: runStagingKey,
          mongoMediaId,
          originalMediaId,
        });
      }

      const runMediaId =
        image.stagingKey || image.publicId || image.public_id || originalMediaId;
      return { mediaId: runMediaId, url: sourceUrl, oldPublicId: originalMediaId };
    }
    
    // Image is not 8:11 ratio - need to auto-crop
    console.log(' Image is not 8:11 ratio, auto-cropping and uploading...');
    
    const croppedBlob = await autoCropImage(sourceUrl);
    
    const uploadResult = await uploadUserImage(
      croppedBlob,
      image.filename || image.originalFilename || 'auto-cropped-image.jpg',
      uploadOptions,
    );
    const autoCropRef = buildCropUploadRef(uploadResult) || uploadResult;
    const uploadedKey = autoCropRef.stagingKey || autoCropRef.publicId;

    if (isStagingObjectKey(uploadedKey) && mongoMediaId) {
      return buildStagingRunpodItem({
        image,
        url: autoCropRef.url,
        stagingKey: uploadedKey,
        mongoMediaId,
        originalMediaId,
      });
    }

    console.log(' Auto-cropped and uploaded:', autoCropRef.url, 'publicId:', autoCropRef.publicId);
    return { mediaId: autoCropRef.publicId, url: autoCropRef.url, oldPublicId: originalMediaId };

  } catch (error) {
    console.error('Error processing image:', error);
    throw error;
  }
};

/**
 * Process all images for /run endpoint
 * @param {Array} images - Array of image objects
 * @param {Object} [options] - { isCover: boolean }
 * @returns {Promise<Array<{mediaId: string, url: string}>>}
 */
export const processAllImagesForRun = async (coverImages, bookId, { isCover = false } = {}) => {
  try {
    console.log(' Processing all images for /run endpoint...');
    
    const processedUrls = await Promise.all(
      coverImages.map(async (image) => {
        const result = await processImageForRun(image, bookId, { isCover });
        const ordering = image.gridOrdering ?? image.ordering;
        const clientImageId = image.id || image._id || null;
        const withClient =
          clientImageId != null && String(clientImageId).trim() !== ''
            ? { ...result, clientImageId: String(clientImageId) }
            : result;
        if (
          ordering !== undefined &&
          ordering !== null &&
          Number.isFinite(Number(ordering))
        ) {
          return { ...withClient, ordering: Number(ordering) };
        }
        return withClient;
      }),
    );

    console.log(' All images processed:', processedUrls);
    return processedUrls;
  } catch (error) {
    console.error('Error processing images:', error);
    throw error;
  }
};
